import { useEffect, useRef, useState, type Ref } from 'react';
import { cn } from './lib/cn';
import {
  PHONE_COUNTRIES,
  countryName,
  dialCodeLength,
  findPhoneCountry,
  flagEmoji,
  joinPhone,
  splitPhone,
} from './lib/phone-countries';
import { SearchSelect } from './SearchSelect';
import type { SelectOption } from './Select';
import { TextField } from './TextField';

/* The dial code is what shows; the country name is what a search finds. */
const OPTIONS: SelectOption[] = PHONE_COUNTRIES.map(country => ({
  value: country.iso,
  label: country.dialCode,
  description: countryName(country.iso),
  leading: (
    <span aria-hidden className="sk:text-sm sk:leading-none">
      {flagEmoji(country.iso)}
    </span>
  ),
}));

function matchCountry(option: SelectOption, query: string) {
  const text = query.trim().toLowerCase().replace(/^\+/, '');
  return (
    option.label.replace('+', '').startsWith(text) ||
    option.value.toLowerCase() === text ||
    (option.description ?? '').toLowerCase().includes(text)
  );
}

/** A name for screen readers alone, or the id of a visible label. */
type PhoneFieldName =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-labelledby': string; 'aria-label'?: never };

export type PhoneFieldProps = PhoneFieldName & {
  'aria-describedby'?: string;
  /** Digits with the dial code first and no "+", e.g. "15551234567"; empty for none. */
  value: string;
  onChange: (value: string) => void;
  /** ISO code (any case) of the country an empty field starts on. The US when unset. */
  defaultCountry?: string;
  /** How many digits the chosen dial code has, for rules on the number's length. */
  onDialCodeChange?: (length: number) => void;
  onBlur?: () => void;
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  /** The number's `<input>`, e.g. for a form library to focus. */
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
};

/**
 * A phone number: the country's dial code, searchable by country, beside the
 * number. Keeps the value as digits with the dial code first, the form the
 * API stores, and an empty value when no number is typed.
 */
export function PhoneField({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  value,
  onChange,
  defaultCountry,
  onDialCodeChange,
  onBlur,
  placeholder,
  isDisabled,
  isInvalid,
  isRequired,
  inputRef,
  className,
}: PhoneFieldProps) {
  /* Picked by hand: wins over the default, and holds while the number is empty. */
  const [picked, setPicked] = useState<string | null>(null);
  const { country, national } = splitPhone(value, picked ?? defaultCountry);
  const length = dialCodeLength(country);

  const reportRef = useRef(onDialCodeChange);
  reportRef.current = onDialCodeChange;
  useEffect(() => {
    reportRef.current?.(length);
  }, [length]);

  const numberProps = {
    'aria-describedby': ariaDescribedBy,
    inputRef,
    value: national,
    onChange: (text: string) => onChange(joinPhone(country, text)),
    onBlur,
    placeholder,
    inputMode: 'tel' as const,
    autoComplete: 'tel-national',
    isDisabled,
    isInvalid,
    isRequired,
    validationBehavior: 'aria' as const,
  };

  return (
    <div
      className={cn(
        'sk:grid sk:w-full sk:grid-cols-[112px_minmax(0,1fr)] sk:gap-2',
        className,
      )}
    >
      <SearchSelect
        aria-label="Country code"
        options={OPTIONS}
        value={country.iso}
        filter={matchCountry}
        onChange={iso => {
          const next = findPhoneCountry(iso);
          if (!next) return;
          setPicked(next.iso);
          onChange(joinPhone(next, national));
        }}
        onBlur={onBlur}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        popoverClassName="sk:w-72"
      />
      {ariaLabelledBy ? (
        <TextField aria-labelledby={ariaLabelledBy} {...numberProps} />
      ) : (
        <TextField label={ariaLabel ?? 'Phone number'} {...numberProps} />
      )}
    </div>
  );
}
